/**
 * AttentionContext (UX6) — the caller's "Needs my attention" list: documents put on hold and
 * addressed to them (`hold.assignedTo == me`). Polled once here and shared so the home queue's
 * attention section and the sidebar badge read the same data without double-fetching.
 *
 * In-app only (decision D2): no email/outbound notification. Polling pauses while the tab is
 * hidden to avoid wasting request units.
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { authFetch } from '../services/authFetch';

/** A pending takeover request raised by another analyst on a doc I put on hold. */
export interface TakeoverRequest {
  requestedBy: string;
  requestedByName?: string;
  requestedAt: string;
}

/**
 * An item in the caller's "Needs my attention" list (server shape from GET /api/attention).
 * Two kinds:
 *  • 'assigned'         — a doc held FOR me (I'm the receiver): resume or clear.
 *  • 'takeover_request' — a doc I put on hold that someone asked to take over: reassign or clear.
 */
export interface AttentionItem {
  kind: 'assigned' | 'takeover_request';
  id: string;
  documentId: string;
  fileName: string;
  documentType: string;
  comment: string;
  // 'assigned' (receiver) fields
  heldBy?: string;
  heldByName?: string;
  heldAt?: string;
  // 'takeover_request' (sender) fields
  assignedTo?: string;
  assignedToName?: string;
  requests?: TakeoverRequest[];
}

interface AttentionContextValue {
  items: AttentionItem[];
  count: number;
  loading: boolean;
  /** Force an immediate refetch (e.g. right after resolving a hold). */
  refresh: () => void;
}

const AttentionContext = createContext<AttentionContextValue | undefined>(undefined);

const POLL_INTERVAL_MS = 20_000;

export const AttentionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<AttentionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await authFetch('/attention');
      if (!response.ok) return; // leave the last-known list in place on a transient failure
      const json = await response.json();
      setItems(Array.isArray(json.data) ? json.data : []);
    } catch {
      // Network hiccup — keep the last-known list rather than clearing the badge.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();

    const startPolling = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(load, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        load();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load]);

  return (
    <AttentionContext.Provider value={{ items, count: items.length, loading, refresh: load }}>
      {children}
    </AttentionContext.Provider>
  );
};

export function useAttention(): AttentionContextValue {
  const ctx = useContext(AttentionContext);
  if (!ctx) {
    throw new Error('useAttention must be used within an AttentionProvider');
  }
  return ctx;
}
